import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@common/components/Button';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { I18n } from 'react-redux-i18n';

import ClassManagement from './containers/ClassManagement';
import { ACTION_TYPES } from '@common/constants';

import '../../styles/Security.scss';
 
const DEFAULT_HIGHLIGHTED_CLASS_VALUE = {};

export default class Security extends Component {
    static propTypes = {
        getSecurity: PropTypes.func.isRequired,
        name: PropTypes.string.isRequired,
        userClasses: PropTypes.array.isRequired,
        commonClasses: PropTypes.array.isRequired,
        onChange: PropTypes.func.isRequired,
        onDelete: PropTypes.func.isRequired,
        isAdmin: PropTypes.bool,
        className: PropTypes.string,
    };

    static defaultProps = {
        isAdmin: false,
    };

    actionType = ACTION_TYPES.create;

    constructor(props) {
        super(props);

        this.state = {
            isOpenClassManagement: false,
            userClasses: props.userClasses,
            highlightedClass: DEFAULT_HIGHLIGHTED_CLASS_VALUE,
        };
    }

    get controls() {
        const { highlightedClass } = this.state;
        const disabled = isEmpty(highlightedClass);

        return [
            { label: 'Add Class', onClick: this.onAddClick, elementId: 'btn-add-class' },
            { label: 'Edit Class', disabled, onClick: this.onEditClick, elementId: 'btn-edit-class' },
            { label: 'Delete Class', disabled, onClick: this.onDeleteClick, elementId: 'btn-delete-class' },
        ];
    }

    componentDidMount() {
        this.props.getSecurity();
    }

    componentDidUpdate(prevProps) {
        if (this.props.name !== prevProps.name) {
            this.setState((state) => ({
                ...state,
                userClasses: this.props.userClasses,
            }));
        }

        const { commonClasses: currCommonClasses } = this.props;
        const { commonClasses: prevCommonClasses } = prevProps;
        
        if (currCommonClasses.length !== prevCommonClasses.length) {
            const { highlightedClass } = this.state;
            const shouldResetHighlightedClass = !(currCommonClasses.find((item => item.id === highlightedClass.id)));

            if (shouldResetHighlightedClass) {
                this.setState((state) => ({
                    ...state,
                    highlightedClass: DEFAULT_HIGHLIGHTED_CLASS_VALUE
                }));
            }
        }

        this.updateHighlightedClass();
    }

    openClassManagement = () => {
        this.setState(state => ({ ...state, isOpenClassManagement: true }));
    }

    closeClassManagement = () => {
        this.setState(state => ({ ...state, isOpenClassManagement: false }));
    }

    updateHighlightedClass = () => {
        if (this.props.isAdmin) {
            return;
        }

        const { commonClasses } = this.props;
        const { highlightedClass } = this.state;
        const updatedClass = commonClasses.find((item) => item.id === highlightedClass.id);

        if (!isEmpty(updatedClass) && !isEqual(updatedClass, highlightedClass)) {
            this.setState((state) => ({
                ...state,
                highlightedClass: updatedClass,
            }));
        }
    }

    onAddClick = () => {
        this.actionType = ACTION_TYPES.create;
        this.openClassManagement();
    }

    onEditClick = () => {
        this.actionType = ACTION_TYPES.edit;
        this.openClassManagement();
    }

    onDeleteClick = () => {
        this.props.onDelete(this.state.highlightedClass.id);
    }

    onToggle = (e) => {
        if (this.props.isAdmin) {
            return;
        }

        e.persist();

        const id = Number(e.target.id);
        const { userClasses } = this.state;

        if (userClasses.includes(id)) {
            const classes = userClasses.filter((identificator) => identificator !== id);
            this.updateUserClasses(classes);

            return;
        }

        const classes = [...userClasses, id];
        this.updateUserClasses(classes);
    }

    updateUserClasses = (userClasses) => {
        this.setState((state) => ({
            ...state,
            userClasses,
        }));

        this.props.onChange(this.props.name, userClasses);
    }

    onClassHighlight = (e) => {
        e.persist();

        const id = Number(e.currentTarget.dataset.id);
        const { commonClasses } = this.props;
        const currHighlightedClass = commonClasses.find((item) => item.id === id);
        const prevHighlightedClass = this.state.highlightedClass;
        const highlightedClass = currHighlightedClass.id === prevHighlightedClass.id 
            ? DEFAULT_HIGHLIGHTED_CLASS_VALUE 
            : currHighlightedClass;

        this.setState((state) => ({
            ...state,
            highlightedClass,
        }));
    }

    render() {
        const { commonClasses, isAdmin, className } = this.props;
        const { userClasses, highlightedClass, isOpenClassManagement } = this.state;
        const classToManage = {
            [ACTION_TYPES.edit]: highlightedClass,
        };

        return (
            <div className={cx("User-details-settings-security", className)}>
                {isOpenClassManagement && (
                    <ClassManagement 
                        actionType={this.actionType}
                        item={classToManage[this.actionType]}
                        close={this.closeClassManagement}
                    />
                )}
                <div 
                    className={cx(
                        'User-details-settings-security__classes',
                        { 'User-details-settings-security__classes-disabled': isAdmin }
                    )}
                >
                    <section>
                        {commonClasses.map(({ id, name }) => (
                            <div key={id} className="User-details-settings-security__classes__item">
                                <input 
                                    id={id}
                                    type="checkbox" 
                                    checked={userClasses.includes(id) || isAdmin} 
                                    onChange={this.onToggle} 
                                />
                                <label 
                                    data-id={id}
                                    className={cx({
                                        'User-details-settings-security__classes__item-highlighted': highlightedClass.id === id
                                    })}
                                    onMouseUp={this.onClassHighlight}
                                >
                                    <span>{name}</span>
                                </label>
                            </div>
                        ))}
                    </section>
                </div>
                <div className="User-details-settings-security__controls">
                    {this.controls.map(({ label, disabled, onClick, elementId }) => (
                        <Button
                            id={elementId}
                            key={label} 
                            className={cx({ disabled })}
                            onClick={onClick}
                        >
                            {I18n.t(label)}
                        </Button>
                    ))}
                </div>
            </div>
        );
    }
}